import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  AppBar, Toolbar, IconButton, Typography, Badge, Avatar, Menu,
  MenuItem, Divider, Box, Tooltip, Chip, InputBase, Paper, List,
  ListItemButton, ListItemText, ListItemIcon, CircularProgress
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import NotificationsIcon from '@mui/icons-material/Notifications';
import LogoutIcon from '@mui/icons-material/Logout';
import SettingsIcon from '@mui/icons-material/Settings';
import SearchIcon from '@mui/icons-material/Search';
import DescriptionIcon from '@mui/icons-material/Description';
import CloseIcon from '@mui/icons-material/Close';
import { useMsal } from '@azure/msal-react';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { useNavigate } from 'react-router-dom';
import { notificationsApi, searchApi } from '../../services/api';
import { graphRequest } from '../../authConfig';

interface NavbarProps {
  drawerWidth: number;
  onMenuClick: () => void;
}

interface SearchResult {
  id: string;
  contract_name: string;
  contract_type: string;
  status: string;
  counterparty_name: string;
  contract_number?: string;
  expiration_date?: string;
  match_field: string;
  snippet?: string;
}

const STATUS_COLORS: Record<string, string> = {
  active: '#4caf50',
  expired: '#f44336',
  draft: '#9e9e9e',
  terminated: '#f44336',
  suspended: '#ff9800'
};

export default function Navbar({ drawerWidth, onMenuClick }: NavbarProps) {
  const { instance, accounts } = useMsal();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const user = accounts[0];
  const initials = user?.name?.split(' ').map((n: string) => n[0]).slice(0, 2).join('').toUpperCase() || '?';
  const firstName = user?.name?.split(' ')[0] || '';

  // ── Notifications polling ─────────────────────────────────────
  useEffect(() => {
    const fetchUnread = async () => {
      try {
        const res = await notificationsApi.list({ unread_only: true, limit: 1 });
        setUnreadCount(res.data.unread_count || 0);
      } catch {}
    };
    fetchUnread();
    const interval = setInterval(fetchUnread, 60000);
    return () => clearInterval(interval);
  }, []);

  // ── Profile photo ─────────────────────────────────────────────
  useEffect(() => {
    const fetchPhoto = async () => {
      if (!accounts[0]) return;
      let token: string | null = null;
      try {
        const res = await instance.acquireTokenSilent({ ...graphRequest, account: accounts[0] });
        token = res.accessToken;
      } catch {
        try {
          const res = await instance.acquireTokenPopup({ ...graphRequest, account: accounts[0] });
          token = res.accessToken;
        } catch { return; }
      }
      try {
        const photoRes = await fetch('https://graph.microsoft.com/v1.0/me/photo/$value', {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!photoRes.ok) return;
        const contentType = photoRes.headers.get('content-type') || '';
        if (!contentType.startsWith('image/')) return;
        const blob = await photoRes.blob();
        if (blob.size === 0) return;
        setPhotoUrl(URL.createObjectURL(blob));
      } catch {}
    };
    if (accounts.length > 0) fetchPhoto();
  }, [instance, accounts]);

  // ── Debounced global search ───────────────────────────────────
  const runSearch = useCallback(async (q: string) => {
    if (q.trim().length < 2) {
      setSearchResults([]);
      setSearchOpen(false);
      return;
    }
    setSearchLoading(true);
    try {
      const res = await searchApi.global(q.trim(), 10);
      setSearchResults(res.data.results || []);
      setSearchOpen(true);
    } catch {
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  }, []);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const q = e.target.value;
    setSearchQuery(q);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (q.trim().length < 2) {
      setSearchOpen(false);
      setSearchResults([]);
      return;
    }
    debounceRef.current = setTimeout(() => runSearch(q), 350);
  };

  const handleSearchSelect = (result: SearchResult) => {
    setSearchQuery('');
    setSearchOpen(false);
    setSearchResults([]);
    navigate(`/contracts/${result.id}`);
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    setSearchOpen(false);
    setSearchResults([]);
  };

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setSearchOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleLogout = () => {
    instance.logoutRedirect({ postLogoutRedirectUri: window.location.origin });
  };

  const matchLabel: Record<string, string> = {
    contract_name: 'Name',
    counterparty_name: 'Counterparty',
    contract_number: 'Contract #',
    description: 'Description',
    scope_summary: 'Scope',
    document_text: 'Document text',
    field: 'Field'
  };

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        width: { sm: `calc(100% - ${drawerWidth}px)` },
        ml: { sm: `${drawerWidth}px` },
        background: 'linear-gradient(135deg, #1a6fff 0%, #c42bb0 100%)',
        color: 'white'
      }}
    >
      <Toolbar sx={{ gap: 1 }}>
        <IconButton edge="start" onClick={onMenuClick} sx={{ mr: 1, display: { sm: 'none' } }}>
          <MenuIcon />
        </IconButton>

        <Typography variant="h6" fontWeight={600} sx={{ color: 'white', whiteSpace: 'nowrap', flexGrow: 1, display: { xs: 'none', sm: 'block' }, fontSize: { sm: 13, md: 15, lg: 17 } }}>
          Contracts and Obligations Management Platform
        </Typography>

        {/* ── Global Search Bar ── */}
        <Box ref={searchRef} sx={{ width: { xs: '100%', sm: 340, md: 460 }, flexShrink: 0, position: 'relative' }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              bgcolor: 'rgba(255,255,255,0.18)',
              borderRadius: 2,
              px: 1.5,
              py: 0.5,
              border: '1px solid rgba(255,255,255,0.3)',
              '&:focus-within': { bgcolor: 'rgba(255,255,255,0.25)', borderColor: 'rgba(255,255,255,0.6)' }
            }}
          >
            {searchLoading
              ? <CircularProgress size={16} sx={{ color: 'rgba(255,255,255,0.7)', mr: 1 }} />
              : <SearchIcon sx={{ color: 'rgba(255,255,255,0.7)', mr: 1, fontSize: 20 }} />
            }
            <InputBase
              value={searchQuery}
              onChange={handleSearchChange}
              onFocus={() => { if (searchResults.length > 0) setSearchOpen(true); }}
              placeholder="Search contracts, text, parties…"
              sx={{ color: 'white', flex: 1, fontSize: 14, '& input::placeholder': { color: 'rgba(255,255,255,0.6)', opacity: 1 } }}
              inputProps={{ 'aria-label': 'search contracts' }}
            />
            {searchQuery && (
              <IconButton size="small" onClick={handleClearSearch} sx={{ color: 'rgba(255,255,255,0.7)', p: 0.25 }}>
                <CloseIcon fontSize="small" />
              </IconButton>
            )}
          </Box>

          {/* Search Results Dropdown */}
          {searchOpen && (
            <Paper
              elevation={8}
              sx={{
                position: 'absolute',
                top: 'calc(100% + 6px)',
                left: 0,
                right: 0,
                zIndex: 1400,
                maxHeight: 420,
                overflowY: 'auto',
                borderRadius: 2
              }}
            >
              {searchResults.length === 0 ? (
                <Box px={2} py={1.5}>
                  <Typography variant="body2" color="text.secondary">No results for "{searchQuery}"</Typography>
                </Box>
              ) : (
                <>
                  <Box px={2} pt={1} pb={0.5}>
                    <Typography variant="caption" color="text.secondary">
                      {searchResults.length} result{searchResults.length !== 1 ? 's' : ''}
                    </Typography>
                  </Box>
                  <List dense disablePadding>
                    {searchResults.map((r, i) => (
                      <React.Fragment key={r.id}>
                        {i > 0 && <Divider component="li" />}
                        <ListItemButton onClick={() => handleSearchSelect(r)} sx={{ py: 1, px: 2 }}>
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            <DescriptionIcon fontSize="small" color="action" />
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Box display="flex" alignItems="center" gap={0.75} flexWrap="wrap">
                                <Typography variant="body2" fontWeight={600} noWrap sx={{ maxWidth: 220 }}>
                                  {r.contract_name}
                                </Typography>
                                <Box
                                  component="span"
                                  sx={{
                                    display: 'inline-block',
                                    width: 8, height: 8, borderRadius: '50%',
                                    bgcolor: STATUS_COLORS[r.status] || '#9e9e9e',
                                    flexShrink: 0
                                  }}
                                />
                                <Typography variant="caption" color="text.secondary" noWrap>
                                  {r.counterparty_name}
                                </Typography>
                                <Chip
                                  label={matchLabel[r.match_field] || r.match_field}
                                  size="small"
                                  sx={{ height: 16, fontSize: 10, ml: 'auto' }}
                                />
                              </Box>
                            }
                            secondary={
                              r.snippet ? (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden',
                                    fontStyle: 'italic'
                                  }}
                                >
                                  {r.snippet}
                                </Typography>
                              ) : undefined
                            }
                          />
                        </ListItemButton>
                      </React.Fragment>
                    ))}
                  </List>
                </>
              )}
            </Paper>
          )}
        </Box>

        {/* Notifications */}
        <Tooltip title="Notifications">
          <IconButton onClick={() => navigate('/notifications')} sx={{ color: 'white' }}>
            <Badge badgeContent={unreadCount} color="error" max={99}>
              <NotificationsIcon />
            </Badge>
          </IconButton>
        </Tooltip>

        {/* User menu */}
        <Box
          onClick={(e) => setAnchorEl(e.currentTarget)}
          sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', cursor: 'pointer', ml: 0.5, px: 0.5 }}
        >
          <Avatar
            src={photoUrl || undefined}
            imgProps={{ onError: () => setPhotoUrl(null) }}
            sx={{ width: 36, height: 36, bgcolor: 'rgba(255,255,255,0.25)', fontSize: 14, fontWeight: 600 }}
          >
            {initials}
          </Avatar>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.9)', fontSize: 10, lineHeight: 1.2, mt: 0.25, whiteSpace: 'nowrap' }}>
            {firstName}
          </Typography>
        </Box>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={() => setAnchorEl(null)}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          PaperProps={{ elevation: 2, sx: { minWidth: 200, mt: 1 } }}
        >
          <Box px={2} py={1.5}>
            <Typography variant="subtitle2" fontWeight={600}>{user?.name}</Typography>
            <Typography variant="caption" color="text.secondary">{user?.username}</Typography>
          </Box>
          <Divider />
          <MenuItem onClick={() => { setAnchorEl(null); navigate('/notifications'); }}>
            <NotificationsIcon fontSize="small" sx={{ mr: 1.5 }} />
            Notifications
            {unreadCount > 0 && <Chip label={unreadCount} size="small" color="error" sx={{ ml: 'auto', height: 18 }} />}
          </MenuItem>
          <MenuItem onClick={() => { setAnchorEl(null); navigate('/preferences'); }}>
            <SettingsIcon fontSize="small" sx={{ mr: 1.5 }} />
            Preferences
          </MenuItem>
          <Divider />
          <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
            <LogoutIcon fontSize="small" sx={{ mr: 1.5 }} />
            Sign Out
          </MenuItem>
        </Menu>
      </Toolbar>
    </AppBar>
  );
}
